import { RRule, RRuleSet } from "./rrule/rrule.js";
//import {RRule,RRuleSet} from "./rrule/rrule271.js";

import * as DateUtils from "../lib/DateUtils.ts";
import type { RecurrencyDescriptor } from "../server/fizapilib.auto.ts";

type RecurrencyTypeEnum = 'YEARLY' | 'MONTHLY' | 'BIWEEKLY' | 'WEEKLY' | 'DAILY' | 'NONE';

interface DateRange {
  start: Date | number;
  end: Date | number;
}

interface IHasRecurrency {
  startDate: Date;
  endDate?: Date | null;
  allDay?: boolean;
  recurrencyDescriptor: RecurrencyDescriptor;
  metaId?: string;
  recurrencyExceptionMap?: Map<number, IHasRecurrency>;
}

interface IHasRecurrencyOccurrence extends IHasRecurrency {
  start: Date;
  end: Date;
  occurenceIndex: number;
}

/**
 * Set hours on a date while preserving other components
 */
function setHours(date: Date, hour: number): Date {
  const result = new Date(date.getFullYear(), date.getMonth(), date.getDate(), hour, date.getMinutes(), date.getSeconds());
  return result;
}

/**
 * Helper class for working with recurring events/tasks
 * IHasRecurrency interface includes:
 *  - startDate: Date
 *  - endDate: Date | null
 *  - allDay: boolean
 *  - recurrencyDescriptor: RecurrencyDescriptor
 */
class IHasRecurrencyHelper {
  static getRange(recurrencyTypeEnum: RecurrencyTypeEnum, date: Date): DateRange {
    switch (recurrencyTypeEnum) {
      case 'YEARLY':
        return DateUtils.getYearRange(date);
      case 'MONTHLY':
        return DateUtils.getMonthRange(date);
      case 'BIWEEKLY':
        return DateUtils.getWeekRange(date, undefined, 2);
      case 'WEEKLY':
        return DateUtils.getWeekRange(date);
      case 'DAILY':
        return DateUtils.getDayRange(date);
      default:
        return DateUtils.getFullRange();
    }
  }

  static getClosest(iHasRecurrency: IHasRecurrency, date: Date): IHasRecurrency | IHasRecurrencyOccurrence {
    const occurrences = this.listInterval(iHasRecurrency, { start: iHasRecurrency.startDate, end: date }, 2);
    if (occurrences.length === 0) {
      return iHasRecurrency;
    } else {
      occurrences.sort((a, b) => Math.abs(a.start.getTime() - date.getTime()) - Math.abs(b.start.getTime() - date.getTime()));
      return occurrences[0];
    }
  }

  static listInterval(iHasRecurrency: IHasRecurrency, range: DateRange, extra: number = 0): IHasRecurrencyOccurrence[] {
    // case of task without duedate
    if (!iHasRecurrency.startDate) {
      return [];
    }
    if (extra === 0) {
      if (iHasRecurrency.startDate > range.end) {
        return [];
      }
      // end before range
      if (iHasRecurrency.endDate && iHasRecurrency.endDate < range.start && !iHasRecurrency.recurrencyDescriptor.rrule) {
        return [];
      }
    }

    const result: IHasRecurrencyOccurrence[] = [];
    const recurrencyDescriptor = iHasRecurrency.recurrencyDescriptor;

    // Constructor function for prototypal inheritance
    const proto = function (this: any) { } as any;

    if (recurrencyDescriptor.rrule) {
      proto.prototype = iHasRecurrency;

      const date = iHasRecurrency.startDate;
      const hours = iHasRecurrency.startDate.getHours();
      const duration = iHasRecurrency.endDate ? iHasRecurrency.endDate.getTime() - iHasRecurrency.startDate.getTime() : 0;
      const durationInDays = Math.round(duration / 86400000);
      const exdate = recurrencyDescriptor.exdate ? recurrencyDescriptor.exdate.map((d) => d.getTime()) : []; // seems that indexOf for Date does not work, so we use the timeinmilis
      const recurrencyDeletedOccurence = recurrencyDescriptor.recurrencyDeletedOccurence || [];
      const options = RRule.parseString(recurrencyDescriptor.rrule);
      const ruleSet = new RRuleSet();
      let LIMIT = 20000;

      if (iHasRecurrency.allDay) {
        // l'heure d'été à aukland met la zone alors on s'assure qu'on sera bien le bon jour avec cette bidouille
        const delta = Math.round(date.getTimezoneOffset() / 120);
        options.dtstart = setHours(date, 12 - delta);
        if (options.until) {
          options.until = new Date(options.until.toISOString().substring(0, 10) + 'T23:59:59');
        }
      } else {
        options.dtstart = date;
      }

      ruleSet.rrule(new RRule(options));

      // we do not use exDate because we need the index in the eventId rebuild
      ruleSet.all((date: Date, idx: number): boolean => {
        if (LIMIT-- <= 0) {
          console.log("recurrency infiniteloop error with ", iHasRecurrency);
          import("../ApiServer.ts").then((ApiServer) => {
            ApiServer.default.webjslog("recurrency infiniteloop error with " + iHasRecurrency.metaId);
          });
          return false;
        }

        date = setHours(date, hours);

        let recurrencyException: IHasRecurrency | undefined;
        // specialday case
        // we have collected exception and put it in this map
        if (iHasRecurrency.recurrencyExceptionMap) {
          if (recurrencyDeletedOccurence.indexOf(idx) !== -1) {
            recurrencyException = iHasRecurrency.recurrencyExceptionMap.get(idx);
            // if the exception was deleted we continue
            if (!recurrencyException) {
              return true;
            }
            date = recurrencyException.startDate;
          }
        } else {
          // for google | outlook we do not have
          if (exdate.indexOf(date.getTime()) !== -1) {
            return true;
          }
          if (recurrencyDeletedOccurence.indexOf(idx) !== -1) {
            return true;
          }
        }

        if (date >= range.end) {
          // we are after the range we stop the iteration
          if (extra <= 0) {
            return false;
          } else {
            extra--;
          }
        }

        let endDate = new Date(date.getTime() + duration);
        if (iHasRecurrency.allDay) {
          endDate = new Date(date.getFullYear(), date.getMonth(), date.getDate() + durationInDays - 1, 23, 59, 59);
        }

        // it is not in the range, we go further
        if (endDate < range.start) {
          return true;
        }
        if (range.start === -Infinity || endDate.getTime() > (range.start as Date).getTime()) {
          if (recurrencyException !== undefined) {
            result.push(recurrencyException as IHasRecurrencyOccurrence);
          } else {
            const instance = new proto() as IHasRecurrencyOccurrence;
            instance.metaId = instance.metaId + "_" + idx;
            instance.start = date;
            instance.end = endDate;
            instance.occurenceIndex = idx;
            result.push(instance);
          }
        }
        return true;
      });
    } else {
      result.push(iHasRecurrency as IHasRecurrencyOccurrence);
    }
    return result;
  }

  static getFirstOccurenceAfter(iHasRecurrency: IHasRecurrency, after: Date): IHasRecurrencyOccurrence | undefined {
    if (!iHasRecurrency.startDate) {
      return undefined;
    }

    const recurrencyDescriptor = iHasRecurrency.recurrencyDescriptor;
    const proto = function (this: any) { } as any;
    const result: IHasRecurrencyOccurrence[] = [];

    if (recurrencyDescriptor.rrule) {
      const date = iHasRecurrency.startDate;
      const hours = iHasRecurrency.startDate.getHours();
      const duration = iHasRecurrency.endDate ? iHasRecurrency.endDate.getTime() - iHasRecurrency.startDate.getTime() : 0;
      const durationInDays = Math.round(duration / 86400000);
      const exdate = recurrencyDescriptor.exdate ? recurrencyDescriptor.exdate.map((d) => d.getTime()) : []; // seems that indexOf for Date does not work, so we use the timeinmilis
      const recurrencyDeletedOccurence = recurrencyDescriptor.recurrencyDeletedOccurence || [];
      const options = RRule.parseString(recurrencyDescriptor.rrule);
      const ruleSet = new RRuleSet();
      let LIMIT = 20000;

      if (iHasRecurrency.allDay) {
        // l'heure d'été à aukland met la zone alors on s'assure qu'on sera bien le bon jour avec cette bidouille
        const delta = Math.round(date.getTimezoneOffset() / 120);
        options.dtstart = setHours(date, 12 - delta);
        if (options.until) {
          options.until = new Date(options.until.toISOString().substring(0, 10) + 'T23:59:59');
        }
      } else {
        options.dtstart = date;
      }

      ruleSet.rrule(new RRule(options));

      // we do not use exDate because we need the index in the eventId rebuild
      ruleSet.all((date: Date, idx: number): boolean => {
        if (LIMIT-- <= 0) {
          console.log("recurrency infiniteloop error with ", iHasRecurrency);
          import("../ApiServer.ts").then((ApiServer) => {
            ApiServer.default.webjslog("recurrency infiniteloop error with " + iHasRecurrency.metaId);
          });
          return false;
        }

        date = setHours(date, hours);
        if (date < after) {
          return true;
        }
        if (recurrencyDeletedOccurence.indexOf(idx) !== -1) {
          if (iHasRecurrency.recurrencyExceptionMap) {
            const exception = iHasRecurrency.recurrencyExceptionMap.get(idx);
            if (exception) {
              result.push(exception as IHasRecurrencyOccurrence);
            }
            return false;
          }
          return true;
        }
        // for google | outlook we do not have
        if (exdate.indexOf(date.getTime()) !== -1) {
          return true;
        }

        let endDate = new Date(date.getTime() + duration);
        if (iHasRecurrency.allDay) {
          endDate = new Date(date.getFullYear(), date.getMonth(), date.getDate() + durationInDays - 1, 23, 59, 59);
        }

        proto.prototype = iHasRecurrency;
        const instance = new proto() as IHasRecurrencyOccurrence;
        instance.metaId = instance.metaId + "_" + idx;
        instance.start = date;
        instance.end = endDate;
        instance.occurenceIndex = idx;
        result.push(instance);
        return false;
      });
      return result[0];
    } else {
      if (iHasRecurrency.startDate < after) {
        return undefined;
      } else {
        return iHasRecurrency as IHasRecurrencyOccurrence;
      }
    }
  }
}

export default IHasRecurrencyHelper;
export type { IHasRecurrency, IHasRecurrencyOccurrence, DateRange, RecurrencyTypeEnum };
